import { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { debounce, pick, startCase } from "lodash";
import { apiPost, apiGet } from "../../Utils/apiFetch";
import { apiPath } from "../../Utils/apiPath";
import AuthContext from "../../context/AuthContext";
import { layerPanel1, user_next_status } from "../../Utils/Constant";
import { useContextSelector } from "use-context-selector";
import { toast } from "react-toastify";

export default function AddUserModal({
  isOpen,
  slug,
  id,
  onClose,
  refreshUsers,
  submitText = "Create",
  title,
}) {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues:{commission:3,exposureLimit:1000}
  });

  const showWebsite = ["owner", "sub_owner"].includes(slug);
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [metaData, setMetaData] = useState();

  const getMetaData = async () => {
    const { status, data } = await apiGet(apiPath.metaData);
    if (status === 200 && data.success) {
      setMetaData(data.results);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setVisiblePassword(false);
    getMetaData();
  }, [isOpen]);

  const filterArray = async (value) => {
    const { status, data } = await apiPost(apiPath.checkProfile, {
      username: value,
    });
    if (status === 200) {
      if (data.success) {
        clearErrors("username");
        return true;
      }
      setError("username", { message: "Username already exists!" });
      return false;
    }
  };

  const debounceFn = useMemo(() => debounce(filterArray, 1000), []);

  const handleUsernameChange = (e) => {
    if (e?.target?.value?.length > 3) {
      debounceFn(e.target.value);
    }
  };

  if (!isOpen) return null;

  const togglePassword = () => setVisiblePassword((prev) => !prev);

  const closeModal = () => {
    reset();
    setVisiblePassword(false);
    onClose?.();
  };

  const submitHandler = async (request) => {
    const ok = await filterArray(request.username);
    if (!ok) return;

        const fields = ["username", "password", "userType", "firstName"];

    if (slug === "user") {
      fields.push("commission");
      fields.push("exposureLimit");
    }

    const obj = pick(request, fields);

    try {
      const { status, data } = await apiPost(apiPath.addProfile, {
        ...obj,
        createdBy: id,
        userType: slug,
      });
      if (status === 200 && data.success) {
        toast.success(data.message);
        reset();
        onClose?.();
        refreshUsers?.();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <div
      id="addUserModal"
      className="pop_bg"
      style={{ height: "100%", width: "100%", display: "block" }}
      onClick={closeModal}
    >
      <div className="pop_box pop_refer" onClick={(e) => e.stopPropagation()}>
        <a
          href="#"
          className="close_pop"
          onClick={(e) => {
            e.preventDefault();
            closeModal();
          }}
        >
          close_pop
        </a>

        <h3 className="modal-header">{title}</h3>

        <form
          noValidate
          style={{ display: "flex", flexFlow: "column" }}
          onSubmit={handleSubmit(submitHandler)}
        >
          <div id="modalid" className="modal-content">
            <ul className="half_box add-member-box">
              <li className="add_account_box">
                <dl>
                  {showWebsite && (
                    <div style={{ position: "relative", marginBottom: "15px" }}>
                      <dt>Website</dt>
                      <dd style={{ position: "relative" }}>
                        <select
                          id="website"
                          {...register("website", {
                            required: "Please select website",
                          })}
                        >
                          <option value="">Please select website</option>
                          {metaData?.websiteList?.map((item, key) => (
                            <option value={item.domain} key={key}>
                              {item.domain}
                            </option>
                          ))}
                        </select>
                        <span className="must rg-mark">＊</span>

                        {errors.website && (
                          <span className="error-text">
                            {errors.website.message}
                          </span>
                        )}
                      </dd>
                    </div>
                  )}

                  {/* Email */}
                  {/* <div style={{ position: "relative", marginBottom: "15px" }}>
                    <dt>Email</dt>
                    <dd style={{ position: "relative" }}>
                      <input
                        id="email"
                        type="email"
                        placeholder="Enter"
                        autoComplete="email"
                        {...register("email", {
                          required: "Please enter email.",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address.",
                          },
                        })}
                      />
                      <span className="must rg-mark">＊</span>
                      {errors.email && (
                        <span id="emailErrorText" className="error-text">
                          {errors.email.message}
                        </span>
                      )}
                    </dd>
                  </div> */}

                  {/* Username */}
                  <div style={{ position: "relative", marginBottom: "15px" }}>
                    <dt>Username</dt>
                    <dd style={{ position: "relative" }}>
                      <input
                        id="userName"
                        type="text"
                        maxLength={30}
                        placeholder="Enter"
                        autoComplete="username"
                        appinputrestriction="noSpecialChars"
                        {...register("username", {
                          required: "Username is required.",
                          minLength: {
                            value: 3,
                            message: "Username must be at least 3 characters.",
                          },
                          maxLength: {
                            value: 30,
                            message: "Username cannot exceed 30 characters.",
                          },
                          pattern: {
                            value: /^[A-Za-z0-9]{3,30}$/,
                            message:
                              "Only letters and numbers allowed, no spaces or special characters.",
                          },
                          onChange: handleUsernameChange,
                        })}
                      />
                      <span className="must rg-mark">＊</span>
                      {errors.username && (
                        <span id="userNameErrorText" className="error-text">
                          {errors.username.message}
                        </span>
                      )}
                    </dd>
                  </div>

                  {/* Password */}
                  <div style={{ position: "relative", marginBottom: "15px" }}>
                    <dt>Password</dt>
                    <dd style={{ position: "relative" }}>
                      <input
                        id="password"
                        type={visiblePassword ? "text" : "password"}
                        maxLength={16}
                        placeholder="Enter"
                        autoComplete="new-password"
                        appinputrestriction="noSpecialChars2"
                        {...register("password", {
                          required: "Password is required.",
                          minLength: {
                            value: 8,
                            message: "Password must be at least 8 characters.",
                          },
                          maxLength: {
                            value: 16,
                            message: "Password cannot exceed 16 characters.",
                          },
                        })}
                      />
                      <span className="must rg-mark">＊</span>
                      <i
                        aria-hidden="true"
                        className={`fa ${
                          visiblePassword ? "fa-eye" : "fa-eye-slash"
                        } fa-xs pass-eye`}
                        style={{
                          color: "#000",
                          top: "7px",
                          right: "10px",
                          position: "absolute",
                          opacity: 0.9,
                          cursor: "pointer",
                        }}
                        onClick={togglePassword}
                      />
                      {errors.password && (
                        <span id="passwordErrorText" className="error-text">
                          {errors.password.message}
                        </span>
                      )}
                    </dd>
                  </div>

                  {/* <div style={{ position: "relative", marginBottom: "15px" }}>
                    <dt>Full Name</dt>
                    <dd style={{ position: "relative" }}>
                      <input
                        id="fullName"
                        type="text"
                        maxLength={30}
                        placeholder="Enter"
                        autoComplete="fullName"
                        appinputrestriction="noSpecialChars"
                        {...register("fullName", {
                          required: "Full Name is required.",
                          minLength: {
                            value: 3,
                            message: "Full Name must be at least 3 characters.",
                          },
                          maxLength: {
                            value: 30,
                            message: "Full Name cannot exceed 30 characters.",
                          },
                          // pattern: {
                          //   value: /^[A-Za-z0-9]{3,30}$/,
                          //   message:
                          //     "Only letters and numbers allowed, no spaces or special characters.",
                          // },
                          // onChange: handleUsernameChange,
                        })}
                      />
                      <span className="must rg-mark">＊</span>
                      {errors.username && (
                        <span id="userNameErrorText" className="error-text">
                          {errors.username.message}
                        </span>
                      )}
                    </dd>
                  </div> */}

                  {/* Confirm Password */}
                  {/* <div style={{ position: "relative", marginBottom: "15px" }}>
                    <dt>Confirm Password</dt>
                    <dd style={{ position: "relative" }}>
                      <input
                        id="confirmPassword"
                        type={visiblePassword ? "text" : "password"}
                        maxLength={16}
                        placeholder="Enter"
                        autoComplete="new-password"
                        {...register("confirmPassword", {
                          required: "Please confirm password.",
                          validate: (value) =>
                            value === watch("password") ||
                            "Passwords do not match.",
                        })}
                      />
                      <span className="must rg-mark">＊</span>
                      {errors.confirmPassword && (
                        <span
                          id="confirmPasswordErrorText"
                          className="error-text"
                        >
                          {errors.confirmPassword.message}
                        </span>
                      )}
                    </dd>
                  </div> */}

                  {/* Phone */}
                  <div style={{ position: "relative", marginBottom: "15px" }}>
                    <dt>Fullname</dt>
                    <dd style={{ position: "relative" }}>
                      <input
                        id="firstName"
                        type="text"
                        placeholder="Enter"
                        {...register("firstName")}
                      />
                      {errors.firstName && (
                        <span className="error-text">
                          {errors.firstName.message}
                        </span>
                      )}
                    </dd>
                  </div>

                  {/* WhatsApp */}
                  {/* <div style={{ position: "relative", marginBottom: "15px" }}>
                    <dt>Whatsapp Number</dt>
                    <dd style={{ position: "relative" }}>
                      <input
                        id="whatsAppNumber"
                        type="text"
                        placeholder="Enter"
                        {...register("whatsAppNumber")}
                      />
                      {errors.whatsAppNumber && (
                        <span className="error-text">
                          {errors.whatsAppNumber.message}
                        </span>
                      )}
                    </dd>
                  </div> */}
                  {slug === "user" && (
                    <>
                <div style={{ position: "relative", marginBottom: "15px" }}>
                    <dt>Commission</dt>
                    <dd style={{ position: "relative" }}>
                      <input
                        id="commission"
                        type="text"
                        placeholder="Enter"
                        {...register("commission")}
                        min="0"
                        max="5"
                        disabled
                      />
                      {errors.commission && (
                        <span className="error-text">
                          {errors.commission.message}
                        </span>
                      )}
                    </dd>
                  </div> 
                   <div
                        style={{ position: "relative", marginBottom: "15px" }}
                      >
                        <dt>Exposure</dt>
                        <dd style={{ position: "relative" }}>
                          <input
                            id="exposureLimit"
                            type="number"
                            placeholder="Enter Exposure Limit"
                            {...register("exposureLimit", {
                              required: "Please enter exposure limit.",
                            })}
                          />
                          {/* {(user?.userType === "super_agent" || isAgent) && (
                            <span className="must rg-mark">＊</span>
                          )} */}
                          {errors.exposureLimit && (
                            <span className="error-text">
                              {errors.exposureLimit.message}
                            </span>
                          )}
                        </dd>
                      </div>
                  </>)}
                  {/* {isAgent && (
                    <>
                      <div
                        style={{ position: "relative", marginBottom: "15px" }}
                      >
                        <dt>Referral Code (optional)</dt>
                        <dd style={{ position: "relative" }}>
                          <input
                            id="refreralCode"
                            type="text"
                            placeholder="Example: referral username"
                            {...register("refreralCode")}
                          />
                        </dd>
                      </div>

                      <div
                        style={{ position: "relative", marginBottom: "15px" }}
                      >
                        <dt>Exposure</dt>
                        <dd style={{ position: "relative" }}>
                          <input
                            id="exposureLimit"
                            type="number"
                            placeholder="Enter Exposure Limit"
                            {...register("exposureLimit", {
                              required: "Please enter exposure limit.",
                            })}
                          />
                          {(user?.userType === "super_agent" || isAgent) && (
                            <span className="must rg-mark">＊</span>
                          )}
                          {errors.exposureLimit && (
                            <span className="error-text">
                              {errors.exposureLimit.message}
                            </span>
                          )}
                        </dd>
                      </div>
                    </>
                  )} */}
                </dl>
              </li>
            </ul>
          </div>

          <div className="modal-footer">
            <div style={{ width: "100%" }} className="btn_box">
              <button
                style={{ marginTop: "0" }}
                type="submit"
                className="btn-send size-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Please wait..." : submitText}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
